import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api.js';
import { googleAuthService } from '../services/googleAuth.js';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTokenExpired, setIsTokenExpired] = useState(false);

  const attemptAutoSignIn = useCallback(async () => {
    if (isTokenExpired && !user) {
      setIsTokenExpired(false);
      try {
        await googleAuthService.initializeOneTap();
      } catch (error) {
        console.error('Auto sign-in failed:', error);
        logout();
        window.location.href = '/';
      }
    }
  }, [isTokenExpired, user]);

  useEffect(() => {
    // Initialize auth from localStorage
    const initAuth = async () => {
      try {
        const savedToken = localStorage.getItem('authToken');
        const savedUser = localStorage.getItem('user');
        
        if (savedToken && savedUser) {
          setToken(savedToken);
          setUser(JSON.parse(savedUser));
          apiService.setAuthToken(savedToken);
        } else {
          // No saved auth, initialize Google One Tap
          try {
            await googleAuthService.initializeOneTap();
          } catch (error) {
            console.error('Failed to initialize Google One Tap:', error);
          }
        }
      } catch (error) {
        // Clear corrupted data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (googleToken) => {
    try {
      setIsLoading(true);
      
      // Call backend to exchange Google token for our token
      const response = await apiService.post('/auth/google-login', {
        token: googleToken,
      });
      
      const { token, user: userData } = response.data;
      const access_token = token?.access_token;
      
      if (!access_token) {
        throw new Error('No access token received from backend');
      }
      
      // Save to state and localStorage
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Set token for future API calls
      apiService.setAuthToken(access_token);
      
      return { success: true, user: userData, token: access_token };
    } catch (error) {
      // Handle specific 403 error for inactive accounts
      if (error.response?.status === 403) {
        return { 
          success: false, 
          error: error.response?.data?.detail || 'Account is inactive. Please contact support.',
          isInactive: true 
        };
      }
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    try {
      setUser(null);
      setToken(null);
      setIsTokenExpired(false);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      apiService.setAuthToken(null);
      googleAuthService.disableOneTap();
    } catch (error) {
      // Logout error - ignore
    }
  };

  const updateUser = (updates) => {
    try {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      // Error updating user - ignore
    }
  };

  useEffect(() => {
    // Listen for token expiration events
    const handleTokenExpired = () => {
      setIsTokenExpired(true);
      attemptAutoSignIn();
    };

    // Listen for One Tap failure events  
    const handleOneTapFailed = () => {
      if (isTokenExpired) {
        // Add delay for Cloud Run cold starts before giving up
        setTimeout(() => {
          if (!localStorage.getItem('authToken')) {
            // One Tap failed and no token after waiting, logout and redirect
            logout();
            window.location.href = '/';
          }
        }, 5000); // Wait 5 more seconds for Cloud Run response
      }
    };

    // Global listener for Google login success (for One Tap auto sign-in)
    const handleGoogleLoginSuccess = async (event) => {
      // Show "logging in" snackbar with no auto-hide (stays until replaced)
      window.dispatchEvent(new CustomEvent('showSnackbar', {
        detail: { message: 'Logging in...', severity: 'info', persistent: true }
      }));
      
      try {
        const result = await login(event.detail.token);
        if (result.success) {
          setIsTokenExpired(false);
          
          // Replace with success snackbar that closes quickly
          window.dispatchEvent(new CustomEvent('showSnackbar', {
            detail: { message: 'Successfully logged in!', severity: 'success', autoHideDuration: 1500 }
          }));
        } else {
          // Replace with error snackbar
          window.dispatchEvent(new CustomEvent('showSnackbar', {
            detail: { message: 'Login failed', severity: 'error' }
          }));
          
          if (isTokenExpired) {
            logout();
            window.location.href = '/';
          }
        }
      } catch (error) {
        // Replace with error snackbar
        window.dispatchEvent(new CustomEvent('showSnackbar', {
          detail: { message: 'Login error', severity: 'error' }
        }));
        
        if (isTokenExpired) {
          logout();
          window.location.href = '/';
        }
      }
    };

    window.addEventListener('authTokenExpired', handleTokenExpired);
    window.addEventListener('googleOneTapFailed', handleOneTapFailed);
    window.addEventListener('googleLoginSuccess', handleGoogleLoginSuccess);

    return () => {
      window.removeEventListener('authTokenExpired', handleTokenExpired);
      window.removeEventListener('googleOneTapFailed', handleOneTapFailed);
      window.removeEventListener('googleLoginSuccess', handleGoogleLoginSuccess);
    };
  }, [attemptAutoSignIn, isTokenExpired, login]);

  const value = {
    user,
    token,
    isLoading,
    isTokenExpired,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    updateUser,
    attemptAutoSignIn,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};